import * as React from 'react'

import { Box, Icon, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import type { Field } from 'factorial-form'
import {
  AccessForSelectFragment,
  ApprovalsValueObjectsApprovalCategoryEnum,
} from 'generated/resources/graphql'

import { AccessSelector } from 'modules/core/components/AccessSelector'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { useAccessApprovalFlows } from 'modules/approvals/hooks/useAccessApprovalFlows'

interface Props {
  category: ApprovalsValueObjectsApprovalCategoryEnum
  approver: AccessForSelectFragment | null | undefined
  field: Field
  startsOn: string
  finishOn: string
  employeeAccessId: number
}

export const DelegateApproverSelector: React.FC<Props> = ({
  category,
  approver,
  field,
  startsOn,
  finishOn,
  employeeAccessId,
}: Props) => {
  const [open, toggle] = useToggle(false)
  const [selectedAccess, setSelectedAccess] = React.useState<
    AccessForSelectFragment | null | undefined
  >(approver)

  const { loading: loadingApprovalFlows, approvalFlows } = useAccessApprovalFlows(
    category,
    employeeAccessId
  )

  const {
    access: { id: currentAccessId },
  } = useCurrentSession()

  const isLoading = loadingApprovalFlows
  const delegateApproverEnabled = approvalFlows?.length > 0

  const handleSelectAccess = (newAccess: AccessForSelectFragment | null) => {
    setSelectedAccess(newAccess)
    field.set(newAccess?.id)
  }

  if (isLoading || !delegateApproverEnabled) {
    return <></>
  }

  const startDate = stringToDate(startsOn, 'ui').format('ll')
  const endDate = stringToDate(finishOn, 'ui').format('ll')
  const excludeCurrentAccess = employeeAccessId === currentAccessId

  return (
    <Box gap="s4" paddingTop="s8">
      <Box flexDirection="row" gap="s4" alignItems="center">
        <Text size={'200'} color="grey1000">
          {i18n.t('approvals.delegate_approver.title')}
        </Text>
        <Text size={'200'} color="grey700">
          {i18n.t('approvals.delegate_approver.title_2')}
        </Text>
        <Tooltip.Alt
          attachment="top center"
          label={i18n.t('approvals.delegate_approver.description')}
          open={open}
          toggle={toggle}
        >
          <Icon.Medium icon="info-circle" color="grey800" />
        </Tooltip.Alt>
      </Box>
      <AccessSelector
        width="s400"
        label={i18n.t('approvals.delegate_approver.empty_state')}
        emptyLabel={i18n.t('approvals.delegate_approver.empty_state')}
        terminated={false}
        value={selectedAccess}
        excludeCurrentAccess={excludeCurrentAccess}
        withAvatar
        onSelect={handleSelectAccess}
      />

      {field.value && selectedAccess && (
        <Box
          background="champagne400"
          borderRadius={{
            all: 'abs008',
          }}
          textAlign="center"
          paddingX="s24"
          paddingY="s16"
        >
          <Text.Normal>
            {i18n.t('approvals.delegate_approver.delegate_approver_assigned', {
              name: selectedAccess.fullName,
              start_date: startDate,
              end_date: endDate,
            })}
          </Text.Normal>
        </Box>
      )}
    </Box>
  )
}
